import axios from "axios";
import { AlertCircle, Bell, CheckCircle2, Clock3, Globe2, Loader2, LogOut, Menu, Radio, Settings as SettingsIcon, ShieldAlert, UserCircle2, Zap } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const routeMeta = {
  "/dashboard": ["Command Center", "Live operational overview"],
  "/geo": ["Geo Access", "Traffic access controls"],
  "/redirector": ["Redirector Links", "Domains, campaigns, and destinations"],
  "/insights": ["Insights", "Traffic and threat intelligence"],
  "/referral": ["Referral Plan", "Invitations and commissions"],
  "/support": ["Support", "Tickets and assistance"],
  "/billing": ["Billing", "Plan, usage, and transactions"],
  "/notifications": ["Notifications", "Account and system events"],
  "/settings": ["Settings", "Account and security controls"],
  "/admin": ["Administration", "Fleet and tenant controls"],
};

const eventStatusStyles = {
  completed: { icon: CheckCircle2, color: "text-emerald-300", ring: "border-emerald-500/30 bg-emerald-500/10" },
  failed: { icon: AlertCircle, color: "text-rose-300", ring: "border-rose-500/30 bg-rose-500/10" },
  queued: { icon: Clock3, color: "text-amber-300", ring: "border-amber-500/30 bg-amber-500/10" },
};

const eventKindIcon = (kind) => (kind === "domain" ? Globe2 : Zap);

const formatRelative = (iso) => {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "—";
    const diff = Math.max(0, Date.now() - then) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch (_) {
    return "—";
  }
};


export const UserTopbar = ({ pathname, unreadCount, globalBlocks = { count: null, status: "loading" }, onOpenMobile, user, isSuperAdmin, onLogout }) => {
  const navigate = useNavigate();
  const key = Object.keys(routeMeta).find((path) => pathname.startsWith(path)) || "/dashboard";
  const [title, subtitle] = routeMeta[key];
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);
  const [failedCount, setFailedCount] = useState(0);
  const menuRef = useRef(null);
  const bellRef = useRef(null);

  const fetchEvents = useCallback(async () => {
    try {
      setEventsLoading(true);
      setEventsError(null);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/user/system-events?limit=12`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const list = response.data?.events || [];
      setEvents(list);
      setFailedCount(list.filter((e) => e.status === "failed").length);
    } catch (err) {
      setEventsError(err.response?.data?.detail || err.message || "Unavailable");
    } finally {
      setEventsLoading(false);
    }
  }, []);

  // Keep the failed-event badge count accurate even before the dropdown has
  // ever been opened. Poll every 60s in the background.
  useEffect(() => {
    fetchEvents();
    const t = setInterval(fetchEvents, 60_000);
    return () => clearInterval(t);
  }, [fetchEvents]);

  useEffect(() => {
    if (!bellOpen) return undefined;
    const timer = setInterval(fetchEvents, 30_000);
    return () => clearInterval(timer);
  }, [bellOpen, fetchEvents]);

  useEffect(() => {
    if (!menuOpen && !bellOpen) return undefined;
    const handler = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
      if (bellRef.current && !bellRef.current.contains(event.target)) setBellOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen, bellOpen]);

  useEffect(() => { setMenuOpen(false); setBellOpen(false); }, [pathname]);

  const initial = (user?.username || "?").charAt(0).toUpperCase();
  const roleLabel = isSuperAdmin ? "Super Admin" : user?.role === "licensed" ? "Licensed" : user?.role || "User";

  const bellTotal = useMemo(() => (unreadCount || 0) + (events.filter((e) => e.status === "failed").length), [unreadCount, events]);

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-[#1e2b3e] bg-[#070b11]/95 px-4 backdrop-blur-md md:px-6" data-testid="user-shell-topbar">
      <div className="flex min-w-0 items-center gap-3">
        <button type="button" onClick={onOpenMobile} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#1e2b3e] text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300 lg:hidden" aria-label="Open navigation" data-testid="mobile-menu-toggle"><span data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></span></button>
        <div className="min-w-0"><p className="truncate font-orbitron text-sm font-semibold uppercase text-slate-100 md:text-base" data-testid="shell-page-title">{title}</p><p className="hidden truncate text-xs text-slate-500 sm:block" data-testid="shell-page-subtitle">{subtitle}</p></div>
      </div>
      <div className="flex items-center gap-2">
        <div
          className="flex h-9 min-w-[78px] items-center gap-2 rounded-md border border-rose-500/20 bg-rose-500/5 px-2.5 text-rose-300 sm:min-w-[126px] sm:px-3"
          data-testid="global-blocked-24h-counter"
          title={globalBlocks.status === "unavailable" ? "Global block total is temporarily unavailable" : "Global blocks recorded across WizardX in the rolling last 24 hours"}
        >
          <ShieldAlert className="h-4 w-4 shrink-0 text-rose-400" />
          <span className="min-w-[2.5ch] font-mono text-sm font-bold tabular-nums" data-testid="global-blocked-24h-count">
            {globalBlocks.count === null ? "—" : globalBlocks.count.toLocaleString()}
          </span>
          <span className="hidden whitespace-nowrap text-[10px] font-semibold uppercase text-rose-300/80 sm:inline" data-testid="global-blocked-24h-label">Blocked · 24h</span>
          <span className={`ml-auto h-1.5 w-1.5 shrink-0 rounded-full ${globalBlocks.status === "live" ? "bg-emerald-400" : globalBlocks.status === "loading" ? "animate-pulse bg-amber-400" : "bg-slate-600"}`} data-testid="global-blocked-24h-status-dot" />
        </div>
        <div className="hidden items-center gap-2 rounded-md border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-300 md:flex" data-testid="platform-live-status"><Radio className="h-3.5 w-3.5" /><span>Live telemetry</span></div>

        <div className="relative" ref={bellRef}>
          <button
            type="button"
            onClick={() => setBellOpen((open) => !open)}
            className={`relative flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${bellOpen ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-200" : "border-[#1e2b3e] text-slate-400 hover:border-cyan-500/40 hover:text-cyan-300"}`}
            aria-label="Open notifications"
            aria-expanded={bellOpen}
            data-testid="topbar-notifications-button"
          >
            <Bell className="h-5 w-5" />
            {bellTotal > 0 && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-rose-500" data-testid="topbar-notification-indicator" />}
          </button>
          {bellOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full z-40 mt-2 w-[22rem] max-w-[calc(100vw-1rem)] overflow-hidden rounded-md border border-[#1e2b3e] bg-[#0c121d] shadow-2xl shadow-black/60"
              data-testid="topbar-system-events-panel"
            >
              <div className="flex items-center justify-between border-b border-[#1e2b3e] px-3 py-2">
                <div>
                  <p className="font-orbitron text-xs font-semibold uppercase tracking-widest text-cyan-300" data-testid="topbar-system-events-title">System events</p>
                  <p className="text-[10px] text-slate-500">Live cron runs and pool activity</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setBellOpen(false); navigate("/notifications"); }}
                  className="rounded border border-[#1e2b3e] px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-slate-300 hover:border-cyan-500/40 hover:text-cyan-200"
                  data-testid="topbar-system-events-open-all"
                >
                  View all
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto" data-testid="topbar-system-events-list">
                {eventsLoading && !events.length && (
                  <div className="flex items-center justify-center gap-2 px-3 py-6 text-xs text-slate-400" data-testid="topbar-system-events-loading">
                    <Loader2 className="h-4 w-4 animate-spin" /> Fetching live events…
                  </div>
                )}
                {eventsError && !eventsLoading && (
                  <div className="px-3 py-6 text-center text-xs text-rose-300" data-testid="topbar-system-events-error">{eventsError}</div>
                )}
                {!eventsLoading && !eventsError && !events.length && (
                  <div className="px-3 py-6 text-center text-xs text-slate-500" data-testid="topbar-system-events-empty">No system events yet.</div>
                )}
                {events.map((event, index) => {
                  const style = eventStatusStyles[event.status] || eventStatusStyles.queued;
                  const KindIcon = eventKindIcon(event.kind);
                  const StatusIcon = style.icon;
                  return (
                    <div
                      key={`${event.id || event.job}-${index}`}
                      className="flex gap-3 border-b border-[#121a26] px-3 py-2 last:border-b-0 hover:bg-[#121a26]/60"
                      data-testid={`topbar-system-event-${index}`}
                    >
                      <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border ${style.ring}`}>
                        <KindIcon className={`h-3.5 w-3.5 ${style.color}`} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-100" data-testid={`topbar-system-event-label-${index}`}>{event.label}</p>
                          <span className="whitespace-nowrap text-[10px] text-slate-500" data-testid={`topbar-system-event-time-${index}`}>{formatRelative(event.at)}</span>
                        </div>
                        <p className="mt-0.5 line-clamp-2 text-xs text-slate-400" data-testid={`topbar-system-event-message-${index}`}>{event.message}</p>
                        <p className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500">
                          <StatusIcon className={`h-3 w-3 ${style.color}`} /> {event.status || "queued"}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className={`flex h-9 items-center gap-2 rounded-md border px-2 transition-colors ${menuOpen ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-200" : "border-[#1e2b3e] bg-[#0c121d] text-slate-200 hover:border-cyan-500/40 hover:text-cyan-200"}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Open user menu"
            data-testid="topbar-user-menu-trigger"
          >
            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded border text-[11px] font-bold ${isSuperAdmin ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-cyan-500/40 bg-cyan-500/10 text-cyan-300"}`} data-testid="topbar-user-avatar">
              {initial}
            </span>
            <span className="hidden min-w-0 flex-col items-start pr-1 sm:flex">
              <span className="max-w-[9rem] truncate text-[11px] font-semibold leading-tight text-slate-100" data-testid="topbar-user-username">{user?.username || "Account"}</span>
              <span className="text-[9px] uppercase tracking-wide text-slate-500" data-testid="topbar-user-role">{roleLabel}</span>
            </span>
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full z-40 mt-2 w-56 origin-top-right overflow-hidden rounded-md border border-[#1e2b3e] bg-[#0c121d] shadow-2xl shadow-black/60"
              data-testid="topbar-user-menu"
            >
              <div className="border-b border-[#1e2b3e] px-3 py-3">
                <p className="truncate text-sm font-semibold text-slate-100" data-testid="topbar-user-menu-username">{user?.username || "Account"}</p>
                <p className="truncate text-[11px] text-slate-500" data-testid="topbar-user-menu-email">{user?.email || "—"}</p>
                <p className="mt-1 inline-flex items-center gap-1 rounded border border-cyan-500/25 bg-cyan-500/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-cyan-300" data-testid="topbar-user-menu-role">{roleLabel}</p>
              </div>
              <nav className="py-1 text-sm">
                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); navigate("/admin"); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-slate-300 transition-colors hover:bg-cyan-500/10 hover:text-cyan-200"
                    data-testid="topbar-user-menu-account"
                  >
                    <UserCircle2 className="h-4 w-4" /> Admin console
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); navigate("/settings"); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-slate-300 transition-colors hover:bg-cyan-500/10 hover:text-cyan-200"
                  data-testid="topbar-user-menu-settings"
                >
                  <SettingsIcon className="h-4 w-4" /> Settings
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onLogout?.(); }}
                  className="flex w-full items-center gap-2 border-t border-[#1e2b3e] px-3 py-2 text-left text-rose-300 transition-colors hover:bg-rose-500/10"
                  data-testid="topbar-user-menu-logout"
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
